"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useCurrency } from "@/hooks/use-currency";
import { orpc } from "@/utils/orpc.client";
import { QUERY_STALE_TIMES } from "@/utils/query-policies";

export interface ListingInfo {
  count: number;
  minPrice: string;
  maxPrice: string;
  currency: string | null;
}

export type ListingMap = Map<string, ListingInfo>;

interface UseListingCountsOptions {
  workIds: string[];
  enabled?: boolean;
}

export function useListingCounts({ workIds, enabled = true }: UseListingCountsOptions): {
  listingMap: ListingMap;
  isLoading: boolean;
} {
  const { currency } = useCurrency();
  const queryWorkIds = useMemo(() => [...new Set(workIds)].sort(), [workIds]);

  const query = useQuery(
    orpc.marketplace.getListingCounts.queryOptions({
      input: { workIds: queryWorkIds, currency },
      enabled: queryWorkIds.length > 0 && enabled,
      staleTime: QUERY_STALE_TIMES.listingCounts,
    }),
  );

  const listingMap = useMemo(() => {
    const map: ListingMap = new Map();
    const data = query.data as
      | {
          workId: string | null;
          count: number;
          minPrice: string;
          maxPrice: string;
          currency: string | null;
        }[]
      | undefined;
    if (!data) return map;
    for (const row of data) {
      const id = row.workId;
      if (!id) continue;
      const existing = map.get(id);
      if (existing) {
        existing.count += row.count;
        if (Number(row.minPrice) < Number(existing.minPrice)) existing.minPrice = row.minPrice;
        if (Number(row.maxPrice) > Number(existing.maxPrice)) existing.maxPrice = row.maxPrice;
      } else {
        map.set(id, {
          count: row.count,
          minPrice: row.minPrice,
          maxPrice: row.maxPrice,
          currency: row.currency,
        });
      }
    }
    return map;
  }, [query.data]);

  return { listingMap, isLoading: query.isLoading };
}
